import type {
  MultiLanguageChronicleItem,
  DisplayMode,
  HttpsUrlString,
  Rfc3339DateTimeString,
  StyledText,
  TextStyle,
} from "../types";

export const DISPLAY_MODES: ReadonlyArray<DisplayMode> = ["cover", "fit"];

const TEXT_STYLES: ReadonlyArray<TextStyle> = [
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "normal",
];

/**
 * Checks if the given value is an object and not `null`.
 * @example
 * isObject({ key: "value" }); // true
 * isObject(null); // false
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Checks if the given value is a `TranslatedString` type.
 * @example
 * isTranslatedString({ "de-DE": "Hello", "de-AT": "Hallo", "es-ES": "Hola" }); // true
 * isTranslatedString({ "de-DE": 123 }); // false
 * @param value - Value to check.
 * @returns `true` when all values are strings.
 */
export function isTranslatedString(
  value: unknown,
): value is Record<string, string> {
  return (
    isObject(value) && Object.values(value).every((x) => typeof x === "string")
  );
}

/**
 * Checks whether a value matches the `StyledText<TranslatedString>` shape.
 * @example
 * isStyledText({ text: { "de-DE": "Titel" }, style: "bold" }); // true
 * isStyledText({ text: "Titel", style: "bold" }); // false
 * @param value - Value to check.
 * @returns `true` when the value is a valid styled translated text object.
 */
export function isStyledText(
  value: unknown,
): value is StyledText<Record<string, string>> {
  if (!isObject(value)) return false;
  if (!isTranslatedString(value.text)) return false;
  if (value.style != null) {
    return TEXT_STYLES.includes(value.style as TextStyle);
  }
  return true;
}

/**
 * Checks whether the given value is a valid HTTPS URL string.
 * @example
 * isValidHttpsUrl("https://www.check24.com/image.webp"); // true
 * isValidHttpsUrl("http://www.check24.com/image.webp"); // false
 * @param url - Value to validate.
 * @returns `true` when `url` is a valid HTTPS URL.
 */
export function isValidHttpsUrl(url: unknown): url is HttpsUrlString {
  if (typeof url !== "string") return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "https:";
  } catch (_e) {
    return false;
  }
}

/**
 * Checks whether a value can be parsed as an RFC3339 date-time string.
 * @example
 * isValidRfc3339("2006-01-02T15:04:05Z"); // true
 * isValidRfc3339("not-a-date"); // false
 * @param dateString - Value to validate.
 * @returns `true` when `dateString` is a valid date-time value.
 */
export function isValidRfc3339(
  dateString: unknown,
): dateString is Rfc3339DateTimeString {
  if (typeof dateString !== "string") return false;
  const parsedDate = new Date(dateString);
  return !Number.isNaN(parsedDate.getTime());
}

/**
 * Validates a chronicle item and rejects with collected validation errors.
 * @example
 * await validateChronicleItem({
 *   target_url: "https://www.check24.com",
 *   primary_image: { url: "https://www.check24.com/image.webp", mode: "cover" },
 *   primary_line: { "de-DE": "Line", "de-AT": "Line", "es-ES": "Linea" },
 *   title: { "de-DE": "Title", "de-AT": "Titel", "es-ES": "Titulo" },
 * });
 * @param item - Value to validate as a chronicle item.
 * @returns A resolved promise when valid; otherwise a rejected promise with errors.
 */
export function validateChronicleItem(item: unknown): Promise<void> {
  const errors: Array<string> = [];

  if (!isObject(item)) {
    return Promise.reject(["Chronicle Item must be an object"]);
  }

  const chronicleItem = item as Record<string, unknown>;

  if (!isObject(chronicleItem.primary_image)) {
    errors.push("primary_image is required and must be an object.");
  } else {
    const primaryImage = chronicleItem.primary_image as Record<string, unknown>;
    if (typeof primaryImage.url !== "string") {
      errors.push("primary_image.url must be a string.");
    } else if (!isValidHttpsUrl(primaryImage.url)) {
      errors.push("primary_image.url must be a valid HTTPS URL.");
    }
    if (!DISPLAY_MODES.includes(primaryImage.mode as DisplayMode)) {
      errors.push('primary_image.mode must be either "cover" or "fit".');
    }
  }

  if (!isTranslatedString(chronicleItem.primary_line)) {
    errors.push(
      'primary_line is required and must be a TranslatedString: { "de-DE": "..." }',
    );
  }

  if (!isTranslatedString(chronicleItem.title)) {
    errors.push(
      'title is required and must be a TranslatedString: { "de-DE": "..." }',
    );
  }

  if (!isValidHttpsUrl(chronicleItem.target_url)) {
    errors.push("target_url must be a valid HTTPS URL string.");
  }

  if (chronicleItem.pricing != null) {
    if (!isObject(chronicleItem.pricing)) {
      errors.push("pricing must be an object");
    } else {
      const pricing = chronicleItem.pricing as Record<string, unknown>;

      if (!Object.prototype.hasOwnProperty.call(pricing, "amount")) {
        errors.push("Missing pricing.amount");
      } else if (!isObject(pricing.amount)) {
        errors.push("pricing.amount must be an object");
      } else {
        const amount = pricing.amount as Record<string, unknown>;

        if (!Object.prototype.hasOwnProperty.call(amount, "amount")) {
          errors.push("Missing pricing.amount.amount");
        } else if (typeof amount.amount !== "number") {
          errors.push("pricing.amount.amount must be a number");
        }

        if (!Object.prototype.hasOwnProperty.call(amount, "currency_code")) {
          errors.push("Missing pricing.amount.currency_code");
        } else if (typeof amount.currency_code !== "string") {
          errors.push("pricing.amount.currency_code must be a string");
        }

        if (
          Object.prototype.hasOwnProperty.call(amount, "show_decimals") &&
          typeof amount.show_decimals !== "boolean"
        ) {
          errors.push("pricing.amount.show_decimals must be boolean if set");
        }
      }

      if (pricing.prefix && !isTranslatedString(pricing.prefix)) {
        errors.push(
          'pricing.prefix must be a TranslatedString: { "de-DE": "..." }',
        );
      }

      if (pricing.suffix && !isTranslatedString(pricing.suffix)) {
        errors.push(
          'pricing.suffix must be a TranslatedString: { "de-DE": "..." }',
        );
      }
    }
  }

  if (
    chronicleItem.expires_at != null &&
    !isValidRfc3339(chronicleItem.expires_at)
  ) {
    errors.push(
      "expires_at must be a valid RFC3339 date time string (UTC time zone).",
    );
  }

  if (
    chronicleItem.secondary_line != null &&
    !isStyledText(chronicleItem.secondary_line)
  ) {
    errors.push(
      'secondary_line must be a StyledText<TranslatedString>: { text: { "de-DE": "..." }, style?: "bold" | "italic" | "underline" | "strikethrough" | "normal" }.',
    );
  }

  if (
    chronicleItem.lazy_loading_url != null &&
    !isValidHttpsUrl(chronicleItem.lazy_loading_url)
  ) {
    errors.push("lazy_loading_url must be a valid HTTPS URL string.");
  }

  if (errors.length !== 0) {
    return Promise.reject(errors);
  }

  return Promise.resolve();
}

/**
 * Validates an item for upsert and enforces upsert-specific constraints.
 * @example
 * await validateOnUpsert(item); // rejects when secondary_line.color is set
 * @param item - Chronicle item to validate before upsert.
 * @returns A resolved promise when the item is valid for upsert.
 */
export function validateOnUpsert(
  item: MultiLanguageChronicleItem,
): Promise<void> {
  return validateChronicleItem(item).then(() => {
    if (
      item.secondary_line &&
      isObject(item.secondary_line) &&
      (item.secondary_line as Record<string, unknown>).color !== undefined
    ) {
      return Promise.reject(
        "You must not set a color when upserting chronicle items",
      );
    }
  });
}
